import { useState, useEffect, useCallback, useRef } from "react";
import { 
  PaperPlaneTilt, 
  Robot, 
  User, 
  Spinner,
  MagnifyingGlass,
  ClipboardText,
  ShieldCheck,
  CaretDown,
  ChatCircle,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from "../ui/select";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../api";
import MarkdownMessage from "../common/MarkdownMessage";
import NotifyWhenDoneButton from "../common/NotifyWhenDoneButton";

const AUSTRALIAN_STATES = [
  { value: "NSW", label: "New South Wales" },
  { value: "VIC", label: "Victoria" },
  { value: "QLD", label: "Queensland" },
  { value: "SA", label: "South Australia" },
  { value: "WA", label: "Western Australia" },
  { value: "TAS", label: "Tasmania" },
  { value: "NT", label: "Northern Territory" },
  { value: "ACT", label: "Australian Capital Territory" },
];

const DRAWING_TYPES = [
  { value: "evacuation_diagram", label: "Evacuation Diagram" },
  { value: "fire_safety", label: "Fire Safety (General)" },
  { value: "structural", label: "Structural" },
  { value: "electrical", label: "Electrical" },
  { value: "hydraulic", label: "Hydraulic / Plumbing" },
  { value: "mechanical", label: "Mechanical / HVAC" },
  { value: "architectural", label: "Architectural" },
  { value: "other", label: "Other" },
];

export default function QAPanel({ 
  sessionId, 
  pdfData, 
  currentPage,
  analysisData,
  researchData,
  onAnalysisComplete,
  onResearchComplete,
  onEvaluationComplete,
  onAnnotationsRefined,
  fasterAnalysis = false,
}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPhase, setCurrentPhase] = useState("waiting");
  const [answers, setAnswers] = useState({});
  const [selectedPage, setSelectedPage] = useState(1);
  const [evaluateAll, setEvaluateAll] = useState(false);
  const messagesEndRef = useRef(null);
  const msgCounter = useRef(0);
  const [chatInput, setChatInput] = useState("");
  const chatInputRef = useRef(null);
  // Refinement state
  const [refining, setRefining] = useState(false);
  const [refineAssessment, setRefineAssessment] = useState(null);
  // Track when a long-running phase started (for notify button delay)
  const [phaseStartedAt, setPhaseStartedAt] = useState(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (currentPage) setSelectedPage(currentPage);
  }, [currentPage]);

  // Start analysis when PDF is uploaded.
  // currentPhase and startAnalysis are intentionally omitted from deps —
  // we only want this to fire when pdfData first arrives, not on every phase change.
  useEffect(() => {
    if (pdfData && currentPhase === "waiting") {
      startAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfData]);

  const addMessage = useCallback((role, content, type = "text") => {
    setMessages(prev => [...prev, { 
      id: ++msgCounter.current,
      role, 
      content, 
      type,
      timestamp: new Date().toISOString()
    }]);
  }, []);

  const sendFollowUp = useCallback(async () => {
    const msg = chatInput.trim();
    if (!msg || loading) return;
    setChatInput("");
    addMessage("user", msg);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: msg
      });
      addMessage("ai", response.data.response);
    } catch {
      addMessage("system", "Failed to get a response. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [chatInput, loading, sessionId, addMessage]);

  const handleChatKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendFollowUp();
    }
  }, [sendFollowUp]);

  const startAnalysis = async () => {
    setCurrentPhase("analyzing");
    setLoading(true);
    addMessage("system", "Starting AI analysis of your drawing...");

    try {
      const formData = new FormData();
      formData.append("session_id", sessionId);
      
      const response = await axios.post(`${API}/analyze-drawing`, formData);
      
      if (response.data.success) {
        addMessage("ai", response.data.initial_observations || "I've examined the drawing.");
        
        if (response.data.status === "needs_input") {
          setCurrentPhase("questions");
          
          // Pre-fill detected info
          const detected = response.data.detected_info || {};
          setAnswers({
            drawing_type: detected.drawing_type || "",
            state: detected.state || "",
            city: detected.city || "",
            project_name: detected.project_name || ""
          });
          
          addMessage("ai", "I need a few more details to ensure accurate evaluation:", "questions");
        } else {
          setCurrentPhase("researching");
          onAnalysisComplete(response.data);
          await conductResearch();
        }
      }
    } catch (error) {
      addMessage("system", "Error analyzing drawing. Please try again.");
      toast.error("Analysis failed");
      setCurrentPhase("waiting");
    } finally {
      setLoading(false);
    }
  };

  const submitAnswers = async () => {
    setLoading(true);
    const displayType = answers.drawing_type === 'other' 
      ? `Other: ${answers.other_description}` 
      : DRAWING_TYPES.find(t => t.value === answers.drawing_type)?.label || answers.drawing_type;
    addMessage("user", `Location: ${answers.state}${answers.city ? `, ${answers.city}` : ''}\nDrawing Type: ${displayType}\nProject: ${answers.project_name || 'Not specified'}`);

    try {
      // If "other" is selected, use the description as the drawing type
      const submissionAnswers = {
        ...answers,
        drawing_type: answers.drawing_type === 'other' ? answers.other_description : answers.drawing_type
      };
      
      const response = await axios.post(`${API}/submit-answers`, {
        session_id: sessionId,
        answers: submissionAnswers
      });

      if (response.data.success) {
        onAnalysisComplete(response.data);
        setCurrentPhase("researching");
        await conductResearch();
      }
    } catch (error) {
      toast.error("Failed to process answers");
    } finally {
      setLoading(false);
    }
  };

  const pollTask = async (taskId, maxWaitMs = 600000) => {
    const start = Date.now();
    while (Date.now() - start < maxWaitMs) {
      await new Promise(r => setTimeout(r, 3000));
      const res = await axios.get(`${API}/task-status/${taskId}`);
      if (res.data.status !== "processing") return res.data;
    }
    throw new Error("Request timed out. Please try again.");
  };

  const conductResearch = async () => {
    addMessage("system", "Research agent is investigating applicable Australian Standards...");
    setLoading(true);
    setPhaseStartedAt(new Date().toISOString());

    try {
      const formData = new FormData();
      formData.append("session_id", sessionId);
      
      const res = await axios.post(`${API}/conduct-research`, formData);
      const data = res.data.task_id ? await pollTask(res.data.task_id) : res.data;

      if (data.status === "error") {
        addMessage("system", `Research failed: ${data.error || "Unknown error"}`);
        setCurrentPhase("ready_to_evaluate");
      } else if (data.success) {
        addMessage("ai", `Research complete for ${data.drawing_type} drawings in ${data.location}.\n\nKey standards identified. Ready to perform compliance evaluation.`, "research");
        onResearchComplete(data);
        setCurrentPhase("ready_to_evaluate");
      } else {
        const detail = data.error ? `: ${data.error}` : "";
        addMessage("system", `Research failed${detail}. Proceeding with evaluation...`);
        setCurrentPhase("ready_to_evaluate");
      }
    } catch (error) {
      addMessage("system", `Research error: ${error.message || "Unknown error"}. Proceeding with evaluation...`);
      setCurrentPhase("ready_to_evaluate");
    } finally {
      setLoading(false);
    }
  };

  const startEvaluation = async () => {
    setCurrentPhase("evaluating");
    setLoading(true);
    setPhaseStartedAt(new Date().toISOString());
    const pageLabel = evaluateAll 
      ? "all pages" 
      : `page ${selectedPage}`;
    addMessage("system", `Performing detailed compliance evaluation (${pageLabel})...`);

    try {
      const formData = new FormData();
      formData.append("session_id", sessionId);
      formData.append("page_num", String(selectedPage));
      formData.append("evaluate_all", String(evaluateAll));
      formData.append("faster_analysis", String(fasterAnalysis));
      
      const res = await axios.post(`${API}/evaluate`, formData);
      const data = res.data.task_id ? await pollTask(res.data.task_id) : res.data;

      if (data.success) {
        addMessage("ai", "Evaluation complete. I've identified compliance issues and recommendations.", "complete");
        if (data.warnings && data.warnings.length > 0) {
          data.warnings.forEach(w => addMessage("system", `Note: ${w}`));
        }
        onEvaluationComplete(data.evaluation);
        setCurrentPhase("complete");

        // Auto-trigger circle refinement in the background (fire-and-forget)
        triggerRefinement();
      } else {
        throw new Error(data.error || "Evaluation failed");
      }
    } catch (error) {
      const msg = error.message || "Evaluation failed";
      addMessage("system", `Evaluation error: ${msg}`);
      toast.error(msg.length > 80 ? msg.slice(0, 80) + "…" : msg);
      setCurrentPhase("ready_to_evaluate");
    } finally {
      setLoading(false);
    }
  };

  const triggerRefinement = useCallback(async () => {
    setRefining(true);
    setRefineAssessment(null);
    try {
      const formData = new FormData();
      formData.append("session_id", sessionId);
      const res = await axios.post(`${API}/refine-annotations`, formData);
      if (!res.data.task_id) return;

      const result = await pollTask(res.data.task_id, 300000); // 5 min max
      if (result.status === "complete") {
        const refined = result.refined_annotations || [];
        if (refined.length > 0 && onAnnotationsRefined) {
          onAnnotationsRefined(refined);
        }
        if (result.assessment) setRefineAssessment(result.assessment);
      }
    } catch {
      // Refinement failure is non-fatal — original circles remain
    } finally {
      setRefining(false);
    }
  }, [sessionId, onAnnotationsRefined, pollTask]);

  return (
    <div className="qa-panel h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2">
          <Robot size={20} weight="fill" className="text-primary" />
          <span className="font-semibold text-slate-800">AI Evaluation Assistant</span>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          {currentPhase === "waiting" && "Upload a drawing to begin analysis"}
          {currentPhase === "analyzing" && "Examining your drawing..."}
          {currentPhase === "questions" && "Please provide additional details"}
          {currentPhase === "researching" && "Researching Australian Standards... (1–2 min)"}
          {currentPhase === "ready_to_evaluate" && "Ready to evaluate compliance"}
          {currentPhase === "evaluating" && "Evaluating against standards... (2–4 min)"}
          {currentPhase === "complete" && !refining && "Evaluation complete"}
          {currentPhase === "complete" && refining && (
            <span className="flex items-center gap-1.5">
              <ArrowsClockwise size={11} className="animate-spin text-primary" />
              Refining circle positions...
            </span>
          )}
        </p>
        {(currentPhase === "researching" || currentPhase === "evaluating") && sessionId && (
          <NotifyWhenDoneButton
            jobType="evaluation"
            sessionId={sessionId}
            startedAt={phaseStartedAt}
          />
        )}
      </div>

      {/* Messages Area */}
      <div className="qa-messages flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && currentPhase === "waiting" && (
          <div className="text-center py-12">
            <ShieldCheck size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 text-sm">
              Upload a technical drawing to start<br />the compliance evaluation
            </p>
          </div>
        )}

        {messages.map((msg) => (
          <div 
            key={msg.id}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div className={`max-w-[85%] ${
              msg.role === "user" 
                ? "message-user px-4 py-3" 
                : msg.role === "system"
                  ? "bg-slate-100 text-slate-600 px-4 py-2 rounded text-sm"
                  : "message-ai px-4 py-3"
            }`}>
              {msg.role === "ai" && (
                <div className="flex items-center gap-2 mb-2">
                  <Robot size={16} className="text-primary" />
                  <span className="text-xs font-medium text-primary">AI Assistant</span>
                </div>
              )}
              {msg.role === "ai" ? (
                <MarkdownMessage content={msg.content} />
              ) : (
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              )}
            </div>
          </div>
        ))}

        {/* Questions Form */}
        {currentPhase === "questions" && (
          <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-4" data-testid="questions-form">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Drawing Type *
              </label>
              <Select 
                value={answers.drawing_type} 
                onValueChange={(v) => setAnswers(prev => ({ ...prev, drawing_type: v, other_description: v !== 'other' ? '' : prev.other_description }))}
              >
                <SelectTrigger className="w-full" data-testid="drawing-type-select">
                  <SelectValue placeholder="Select drawing type" />
                </SelectTrigger>
                <SelectContent>
                  {DRAWING_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Show description field when "Other" is selected */}
            {answers.drawing_type === "other" && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Describe the drawing type *
                </label>
                <Input
                  value={answers.other_description || ""}
                  onChange={(e) => setAnswers(prev => ({ ...prev, other_description: e.target.value }))}
                  placeholder="e.g., Emergency lighting layout, Access control plan"
                  className="rounded-none"
                  data-testid="other-description-input"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                State / Territory *
              </label>
              <Select 
                value={answers.state} 
                onValueChange={(v) => setAnswers(prev => ({ ...prev, state: v }))}
              >
                <SelectTrigger className="w-full" data-testid="state-select">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {AUSTRALIAN_STATES.map(state => (
                    <SelectItem key={state.value} value={state.value}>
                      {state.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                City (optional)
              </label>
              <Input
                value={answers.city || ""}
                onChange={(e) => setAnswers(prev => ({ ...prev, city: e.target.value }))}
                placeholder="e.g., Sydney, Melbourne"
                className="rounded-none"
                data-testid="city-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Project Name (optional)
              </label>
              <Input
                value={answers.project_name || ""}
                onChange={(e) => setAnswers(prev => ({ ...prev, project_name: e.target.value }))}
                placeholder="e.g., Commercial Building - 123 Main St"
                className="rounded-none"
                data-testid="project-name-input"
              />
            </div>

            <Button
              onClick={submitAnswers}
              disabled={!answers.drawing_type || !answers.state || (answers.drawing_type === 'other' && !answers.other_description) || loading}
              className="w-full rounded-sm bg-primary hover:bg-primary-hover"
              data-testid="submit-answers-button"
            >
              {loading ? (
                <Spinner size={18} className="animate-spin" />
              ) : (
                <>
                  Continue Analysis
                  <PaperPlaneTilt size={16} className="ml-2" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* Ready to Evaluate Button */}
        {currentPhase === "ready_to_evaluate" && (
          <div className="bg-primary/5 border border-primary/20 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardText size={20} className="text-primary" />
              <span className="font-medium text-slate-800">Ready for Evaluation</span>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              The research agent has gathered the applicable Australian Standards. 
              Click below to evaluate your drawing against these standards.
            </p>

            {/* Page selection for multi-page PDFs */}
            {pdfData && pdfData.page_count > 1 && (
              <div className="mb-4 space-y-3 bg-white border border-slate-200 rounded-sm p-3">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Evaluation Scope ({pdfData.page_count} pages)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEvaluateAll(false)}
                    className={`flex-1 text-xs py-2 px-3 rounded border transition-colors ${
                      !evaluateAll 
                        ? "bg-primary text-white border-primary" 
                        : "bg-white text-slate-600 border-slate-300 hover:border-primary"
                    }`}
                    data-testid="evaluate-single-page-btn"
                  >
                    Single Page
                  </button>
                  <button
                    onClick={() => setEvaluateAll(true)}
                    className={`flex-1 text-xs py-2 px-3 rounded border transition-colors ${
                      evaluateAll 
                        ? "bg-primary text-white border-primary" 
                        : "bg-white text-slate-600 border-slate-300 hover:border-primary"
                    }`}
                    data-testid="evaluate-all-pages-btn"
                  >
                    All Pages
                  </button>
                </div>
                {!evaluateAll && (
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      Page to evaluate (currently viewing: {currentPage})
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={1}
                        max={pdfData.page_count}
                        value={selectedPage}
                        onWheel={(e) => e.target.blur()}
                        onChange={(e) => {
                          const v = Math.max(1, Math.min(pdfData.page_count, Number(e.target.value)));
                          setSelectedPage(v);
                        }}
                        className="w-20 border border-slate-300 rounded text-sm px-2 py-1 text-center focus:outline-none focus:ring-1 focus:ring-primary"
                        data-testid="page-number-input"
                      />
                      <span className="text-xs text-slate-400">of {pdfData.page_count}</span>
                    </div>
                  </div>
                )}
                {evaluateAll && pdfData.page_count > 5 && (
                  <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    Note: Evaluating many pages may take longer.
                  </p>
                )}
              </div>
            )}

            <Button
              onClick={startEvaluation}
              disabled={loading}
              className="w-full rounded-sm bg-primary hover:bg-primary-hover"
              data-testid="start-evaluation-button"
            >
              {loading ? (
                <Spinner size={18} className="animate-spin" />
              ) : (
                <>
                  <MagnifyingGlass size={16} className="mr-2" />
                  {pdfData && pdfData.page_count > 1 
                    ? evaluateAll ? `Evaluate All ${pdfData.page_count} Pages` : `Evaluate Page ${selectedPage}`
                    : "Evaluate Compliance"
                  }
                </>
              )}
            </Button>
          </div>
        )}

        {/* Refinement assessment — shown after circle refiner completes */}
        {refineAssessment && (
          <div className="bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs text-slate-500 flex items-start gap-2"
               data-testid="refine-assessment">
            <ArrowsClockwise size={13} className="flex-shrink-0 mt-0.5 text-primary" />
            <span><strong className="text-slate-600">Circle refinement:</strong> {refineAssessment}</span>
          </div>
        )}

        {/* Loading indicator */}
        {loading && (
          <div className="flex items-center justify-center py-4">
            <div className="spinner" />
            <span className="ml-3 text-sm text-slate-500">Processing...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Follow-up chat input (visible after evaluation is complete) ── */}
      {currentPhase === "complete" && (
        <div className="qa-input">
          <div className="flex items-center gap-2 mb-2">
            <ChatCircle size={14} className="text-primary" />
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
              Ask a follow-up question
            </span>
          </div>
          <div className="flex gap-2">
            <Input
              ref={chatInputRef}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={handleChatKeyDown}
              placeholder="e.g. How do I fix the assembly point issue?"
              className="flex-1 rounded-sm text-sm"
              disabled={loading}
              data-testid="followup-chat-input"
            />
            <Button
              onClick={sendFollowUp}
              disabled={!chatInput.trim() || loading}
              size="sm"
              className="rounded-sm bg-primary hover:bg-primary-hover flex-shrink-0"
              data-testid="followup-send-button"
            >
              {loading
                ? <Spinner size={15} className="animate-spin" />
                : <PaperPlaneTilt size={15} />
              }
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
